import React from 'react';
import { Button } from './ui/button';
import { heroContent, images } from '../data/mock';
import { ArrowRight, Heart, Shield, Users } from 'lucide-react';

const HeroSection = () => {
  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section className="relative pt-24 md:pt-32 pb-16 md:pb-24 bg-white overflow-hidden">
      {/* Subtle background pattern */}
      <div className="absolute inset-0 opacity-30">
        <div className="absolute top-20 right-10 w-72 h-72 bg-violet-100 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 left-10 w-96 h-96 bg-fuchsia-50 rounded-full blur-3xl"></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Content */}
          <div className="text-left">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-violet-50 rounded-full mb-6">
              <span className="w-2 h-2 bg-violet-500 rounded-full animate-pulse"></span>
              <span className="text-violet-700 text-sm font-medium">Currently serving Florida, Georgia, Texas - and expanding!</span>
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6">
              <span className="text-slate-900">Virtual Nurse Support for </span>
              <span className="text-nursebloc-gradient">Families Caring for Loved Ones</span>
            </h1>
            
            <p className="text-lg md:text-xl text-slate-700 leading-relaxed mb-8 max-w-xl">
              {heroContent.subheadline}
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex flex-col">
                <Button 
                  size="lg"
                  className="btn-nursebloc px-8 py-6 rounded-full text-lg font-semibold shadow-lg group relative"
                  onClick={() => scrollToSection('contact')}
                >
                  <span className="flex items-center">
                    Talk to a Nurse
                    <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </span>
                </Button>
                <div className="flex flex-col items-center mt-3 sm:items-start">
                  <p className="text-base font-semibold text-slate-700 text-center sm:text-left">Free 15–30 minute virtual consult with a real nurse</p>
                  <p className="text-sm text-slate-600 text-center sm:text-left mt-1">Private • No pressure • No obligation</p>
                </div>
              </div>
              <Button 
                size="lg" 
                onClick={() => scrollToSection('how-it-works')}
                className="group bg-white border-2 border-violet-200 text-violet-600 hover:bg-violet-50 transition-all duration-300 shadow-lg hover:shadow-xl"
              >
                <span className="flex items-center">
                  {heroContent.secondaryCta}
                </span>
              </Button>
            </div>

            {/* Trust indicators */}
            <div className="mt-10 pt-8 border-t border-violet-100">
              <p className="text-sm text-gray-500 mb-3">Families trust us with their care</p>
              <div className="flex items-center gap-6">
                <div className="text-center">
                  <p className="text-2xl font-bold text-nursebloc-gradient">50+</p>
                  <p className="text-xs text-gray-500">Families Helped</p>
                </div>
                <div className="w-px h-10 bg-violet-200"></div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-nursebloc-gradient">15+</p>
                  <p className="text-xs text-gray-500">Years Experience</p>
                </div>
                <div className="w-px h-10 bg-violet-200"></div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-nursebloc-gradient">100%</p>
                  <p className="text-xs text-gray-500">Committed to Care</p>
                </div>
              </div>
            </div>
          </div>

          {/* Image */}
          <div className="relative">
            <div className="relative rounded-3xl overflow-hidden shadow-2xl">
              <img 
                src="/hero-image.png" 
                alt="NurseBloc virtual care consultation"
                className="w-full h-[400px] md:h-[500px] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-violet-900/20 to-transparent"></div>
            </div>
            
            {/* Floating card */}
            <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl p-4 shadow-xl border border-violet-100 max-w-[240px]">
              <div className="flex items-start gap-3">
                <div className="w-12 h-12 bg-violet-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <svg className="w-6 h-6 text-violet-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <div>
                  <p className="font-semibold text-slate-800 text-sm">Real nurses, real support</p>
                  <p className="text-xs text-gray-500 mt-1">Experienced RNs ready to help your family</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
